import { useState, type FormEvent } from 'react';

import {
    Button,
    Card,
    Field,
    Input,
    MonoLabel,
    OptionGroup,
    OptionRow,
    Skeleton,
    StatusPill,
} from '@/design-system';
import {
    API_KEY_SCOPES,
    DEFAULT_API_KEY_SCOPES,
    type ApiKey,
    type ApiKeyState,
    type IssuedApiKey,
} from '@/entities/integration';
import { errorMessage, isApiError } from '@/shared/api';
import { formatDayLong, formatDayTimeShort } from '@/shared/lib';
import { ErrorState } from '@/shared/ui';

import { integrationsCopy as copy } from '../copy';
import {
    useApiKeyActivity,
    useApiKeys,
    useCreateApiKey,
    useRevokeApiKey,
    type ApiKeyOwner,
} from '../hooks';
import { CopyButton } from './CopyButton';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';
const TITLE =
    'm-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]';

const STATE_TONES: Record<ApiKeyState, 'accent' | 'neutral' | 'warn'> = {
    active: 'accent',
    expired: 'warn',
    revoked: 'neutral',
};

export interface ApiKeysCardProps {
    owner: ApiKeyOwner;
    /** De sleutel die net gemaakt is; het geheim staat zolang in beeld. */
    issued: IssuedApiKey | null;
    onIssued: (issued: IssuedApiKey | null) => void;
}

/**
 * De API-sleutels: de lijst, een nieuwe maken, en intrekken.
 *
 * Het geheim van een nieuwe sleutel staat één keer in beeld, met een knop om te
 * kopiëren; daarna kent zelfs Smartkeur hem niet meer. In de lijst staat een
 * sleutel als `sk_live_…abcd`, zodat je herkent welke in je config staat.
 * Intrekken vraagt eerst of het zeker is: alles wat de sleutel gebruikt, stopt.
 */
export function ApiKeysCard({ owner, issued, onIssued }: ApiKeysCardProps) {
    const keys = useApiKeys(owner);
    const [adding, setAdding] = useState(false);

    return (
        <Card padding="lg" as="section" aria-labelledby={`api-keys-${owner}`}>
            <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                    <h2 id={`api-keys-${owner}`} className={TITLE}>
                        {copy.keysTitle}
                    </h2>
                    <p className={`m-0 mt-1 ${SMALL}`}>
                        {owner === 'platform' ? copy.platformKeysIntro : copy.keysIntro}
                    </p>
                </div>
                {adding ? null : (
                    <Button
                        variant="outline"
                        size="sm"
                        icon="add"
                        onClick={() => {
                            onIssued(null);
                            setAdding(true);
                        }}
                    >
                        {copy.newKey}
                    </Button>
                )}
            </div>

            {issued ? <IssuedSecret issued={issued} onDone={() => onIssued(null)} /> : null}

            {adding ? (
                <NewKeyForm
                    owner={owner}
                    onCancel={() => setAdding(false)}
                    onCreated={(created) => {
                        setAdding(false);
                        onIssued(created);
                    }}
                />
            ) : null}

            <div className="mt-[10px]">
                {keys.isPending ? (
                    <div aria-busy="true">
                        <Skeleton height={14} label={copy.loading} />
                        <Skeleton width="70%" height={14} className="mt-2" />
                    </div>
                ) : keys.isError ? (
                    <ErrorState
                        error={keys.error}
                        title={copy.keysTitle}
                        onRetry={() => void keys.refetch()}
                    />
                ) : keys.data.keys.length === 0 ? (
                    <p className="m-0 py-[9px] text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                        {copy.empty}
                    </p>
                ) : (
                    <ul className="m-0 list-none p-0">
                        {keys.data.keys.map((key) => (
                            <KeyRow key={key.uuid} owner={owner} apiKey={key} />
                        ))}
                    </ul>
                )}
            </div>
        </Card>
    );
}

function IssuedSecret({ issued, onDone }: { issued: IssuedApiKey; onDone: () => void }) {
    return (
        <Card
            variant="accent"
            padding="md"
            radius={9}
            className="mt-[12px]"
            role="group"
            aria-label={copy.secretTitle(issued.key.name)}
        >
            <p className="m-0 text-[13px] leading-[1.4] font-semibold text-[var(--text-primary)]">
                {copy.secretTitle(issued.key.name)}
            </p>
            <p className="m-0 mt-1 text-[12px] leading-[1.45] text-[var(--text-secondary)]">
                {copy.secretNote}
            </p>
            <div className="mt-[10px] flex flex-wrap items-center gap-2">
                <code
                    data-testid="api-key-secret"
                    className="min-w-0 flex-1 rounded-[6px] border border-[var(--border-subtle)] bg-[var(--surface-card)] px-[10px] py-[9px] font-[family-name:var(--font-mono)] text-[12px] break-all text-[var(--text-primary)] select-all"
                >
                    {issued.secret}
                </code>
                <CopyButton text={issued.secret} />
            </div>
            <Button variant="outline" size="xs" className="mt-[10px]" onClick={onDone}>
                {copy.secretDone}
            </Button>
        </Card>
    );
}

function NewKeyForm({
    owner,
    onCancel,
    onCreated,
}: {
    owner: ApiKeyOwner;
    onCancel: () => void;
    onCreated: (issued: IssuedApiKey) => void;
}) {
    const create = useCreateApiKey(owner);
    const [name, setName] = useState('');
    const [scopes, setScopes] = useState<string[]>([...DEFAULT_API_KEY_SCOPES]);
    const [expiresOn, setExpiresOn] = useState('');
    const [missing, setMissing] = useState<{ name: boolean; scopes: boolean }>({
        name: false,
        scopes: false,
    });

    const fieldError = (field: string) =>
        isApiError(create.error) ? create.error.firstValidationError(field) : undefined;
    const formError =
        create.error && !fieldError('name') && !fieldError('scopes') && !fieldError('expires_at')
            ? errorMessage(create.error)
            : null;

    function submit(event: FormEvent) {
        event.preventDefault();

        const check = {
            name: name.trim() === '',
            scopes: owner === 'company' && scopes.length === 0,
        };
        setMissing(check);

        if (check.name || check.scopes) {
            return;
        }

        create.mutate(
            { name, scopes, expiresOn: expiresOn === '' ? null : expiresOn },
            { onSuccess: onCreated },
        );
    }

    return (
        <form
            onSubmit={submit}
            noValidate
            aria-labelledby={`new-api-key-${owner}`}
            className="mt-[14px] border-t border-[var(--border-subtle)] pt-[14px]"
        >
            <h3
                id={`new-api-key-${owner}`}
                className="m-0 mb-2 font-[family-name:var(--font-display)] text-[13px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {copy.formTitle}
            </h3>

            <div className="flex flex-wrap items-start gap-3">
                <Field
                    label={copy.name}
                    description={copy.nameHint}
                    size="sm"
                    className="min-w-[220px] flex-1"
                    error={missing.name ? copy.nameRequired : fieldError('name')}
                >
                    <Input
                        value={name}
                        placeholder={copy.namePlaceholder}
                        autoComplete="off"
                        maxLength={100}
                        onChange={(event) => setName(event.target.value)}
                    />
                </Field>
                <Field
                    label={copy.expires}
                    description={copy.expiresHint}
                    size="sm"
                    className="min-w-[180px]"
                    error={fieldError('expires_at')}
                >
                    <Input
                        type="date"
                        value={expiresOn}
                        onChange={(event) => setExpiresOn(event.target.value)}
                    />
                </Field>
            </div>

            {owner === 'company' ? (
                <fieldset className="m-0 mt-[12px] border-0 p-0">
                    <legend className="mb-[6px] p-0 text-[12px] font-medium text-[var(--text-secondary)]">
                        {copy.scopes}
                    </legend>
                    <OptionGroup type="check" label={copy.scopes} className="px-0">
                        {API_KEY_SCOPES.map((scope, index) => (
                            <OptionRow
                                key={scope}
                                index={index}
                                checked={scopes.includes(scope)}
                                onSelect={(on) =>
                                    setScopes((current) =>
                                        on
                                            ? [...current, scope]
                                            : current.filter((value) => value !== scope),
                                    )
                                }
                            >
                                <span className="flex min-w-0 flex-col">
                                    <span className="text-[13px] text-[var(--text-primary)]">
                                        {copy.scopeLabels[scope]}
                                    </span>
                                    <span className="font-[family-name:var(--font-mono)] text-[11px] text-[var(--text-tertiary-safe)]">
                                        {scope}
                                    </span>
                                </span>
                            </OptionRow>
                        ))}
                    </OptionGroup>
                    {missing.scopes || fieldError('scopes') ? (
                        <p
                            role="alert"
                            className="m-0 mt-1 text-[12px] leading-[1.4] text-[var(--danger)]"
                        >
                            {missing.scopes ? copy.scopesRequired : fieldError('scopes')}
                        </p>
                    ) : null}
                </fieldset>
            ) : (
                <p className={`m-0 mt-[10px] ${SMALL}`}>{copy.scopesPlatform}</p>
            )}

            <div className="mt-[12px] flex flex-wrap gap-2">
                <Button type="submit" variant="primary" size="sm" disabled={create.isPending}>
                    {create.isPending ? copy.creating : copy.create}
                </Button>
                <Button variant="outline" size="sm" onClick={onCancel}>
                    {copy.cancel}
                </Button>
            </div>

            {formError ? (
                <p role="alert" className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {formError}
                </p>
            ) : null}
        </form>
    );
}

function KeyRow({ owner, apiKey }: { owner: ApiKeyOwner; apiKey: ApiKey }) {
    const [confirming, setConfirming] = useState(false);
    const [showActivity, setShowActivity] = useState(false);
    const revoke = useRevokeApiKey(owner);
    const active = apiKey.state === 'active';

    return (
        <li className="border-b border-[var(--border-subtle-2)] py-[11px]">
            <div className="flex flex-wrap items-center gap-[10px]">
                <span className="min-w-0 flex-1 truncate text-[14px] leading-[1.35] font-medium text-[var(--text-primary)]">
                    {apiKey.name}
                </span>
                <MonoLabel size={10} tone="muted">
                    {apiKey.masked}
                </MonoLabel>
                <StatusPill tone={STATE_TONES[apiKey.state]} size="sm">
                    {copy.states[apiKey.state]}
                </StatusPill>
            </div>
            <p className={`m-0 mt-[2px] ${SMALL}`}>
                {[
                    apiKey.lastUsedAt
                        ? copy.lastUsed(formatDayTimeShort(apiKey.lastUsedAt))
                        : copy.neverUsed,
                    apiKey.expiresAt
                        ? copy.validUntil(formatDayLong(apiKey.expiresAt))
                        : copy.noEnd,
                    apiKey.createdAt
                        ? copy.madeBy(apiKey.createdBy.name, formatDayLong(apiKey.createdAt))
                        : null,
                    apiKey.revokedAt && apiKey.revokedBy
                        ? copy.revokedBy(apiKey.revokedBy.name, formatDayLong(apiKey.revokedAt))
                        : null,
                ]
                    .filter((part): part is string => part !== null)
                    .join(' · ')}
            </p>
            {apiKey.scopeLabels.length > 0 ? (
                <p className="m-0 mt-[4px] text-[12px] leading-[1.45] text-[var(--text-secondary)]">
                    {apiKey.scopeLabels.join(' · ')}
                </p>
            ) : null}

            {confirming ? (
                <Card
                    variant="warn"
                    padding="md"
                    radius={9}
                    className="mt-[10px]"
                    role="group"
                    aria-label={copy.revokeLabel(apiKey.name)}
                >
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                        {copy.revokeBody(apiKey.name)}
                    </p>
                    <div className="mt-[10px] flex flex-wrap gap-2">
                        <Button
                            variant="warn"
                            size="sm"
                            disabled={revoke.isPending}
                            onClick={() =>
                                revoke.mutate(apiKey.uuid, {
                                    onSuccess: () => setConfirming(false),
                                })
                            }
                        >
                            {copy.revokeYes}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                            {copy.cancel}
                        </Button>
                    </div>
                </Card>
            ) : (
                <div className="mt-[8px] flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="xs"
                        aria-expanded={showActivity}
                        onClick={() => setShowActivity((open) => !open)}
                    >
                        {showActivity ? copy.activityHide : copy.activity}
                    </Button>
                    {active ? (
                        <Button
                            variant="outline"
                            size="xs"
                            aria-label={copy.revokeLabel(apiKey.name)}
                            onClick={() => setConfirming(true)}
                        >
                            {copy.revoke}
                        </Button>
                    ) : null}
                </div>
            )}

            {showActivity ? <KeyActivity owner={owner} apiKey={apiKey} /> : null}

            {revoke.error ? (
                <p
                    role="alert"
                    className="m-0 mt-2 text-[12px] leading-[1.45] text-[var(--warn-text)]"
                >
                    {errorMessage(revoke.error)}
                </p>
            ) : null}
        </li>
    );
}

function KeyActivity({ owner, apiKey }: { owner: ApiKeyOwner; apiKey: ApiKey }) {
    const activity = useApiKeyActivity(owner, apiKey.uuid, true);

    return (
        <div
            role="region"
            aria-label={copy.activityTitle(apiKey.name)}
            className="mt-[10px] rounded-[7px] border border-[var(--border-subtle)] bg-[var(--surface-subtle)] p-[10px]"
        >
            {activity.isPending ? (
                <Skeleton height={12} label={copy.activityLoading} />
            ) : activity.isError ? (
                <ErrorState
                    error={activity.error}
                    title={copy.activityTitle(apiKey.name)}
                    onRetry={() => void activity.refetch()}
                />
            ) : activity.data.length === 0 ? (
                <p className={`m-0 ${SMALL}`}>{copy.activityEmpty}</p>
            ) : (
                <ol className="m-0 list-none p-0">
                    {activity.data.map((entry, index) => (
                        <li
                            key={entry.uuid ?? index}
                            className="flex gap-[10px] py-[3px] text-[12px] leading-[1.45]"
                        >
                            <span className="shrink-0 font-[family-name:var(--font-mono)] text-[11px] text-[var(--text-tertiary-safe)]">
                                {entry.at ? formatDayTimeShort(entry.at) : ''}
                            </span>
                            <span className="min-w-0 text-[var(--text-secondary)]">
                                {entry.description}
                            </span>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}
